'use client';

import { getMysteryRoomMapDefinition, normalizeMysteryRoomMapDefinition } from '../world/mapRegistry';
import { scaleMysteryRoomMap } from '../world/scaleMap';
import type {
  MysteryRoomAccessLevel,
  MysteryRoomCustomObjectDefinition,
  MysteryRoomDoorAccessMode,
  MysteryRoomHazardDefinition,
  MysteryRoomInventoryItemId,
  MysteryRoomInteractableKind,
  MysteryRoomLightDefinition,
  MysteryRoomMapDefinition,
  MysteryRoomNpcBehaviorProfile,
  MysteryRoomNpcFaction,
  MysteryRoomSegment,
  MysteryRoomNpcRole,
  MysteryRoomPickupItemKind,
  MysteryRoomRect,
  MysteryRoomResourceKind,
  MysteryRoomResourceSourceKind,
  MysteryRoomTriggerAction,
  MysteryRoomVec2,
  MysteryRoomWorldLabelVisibility,
  MysteryRoomGameMode,
} from '../world/mapTypes';
import { rectCenter, rectContainsPoint } from '../world/mapTypes';
import { npcUsesZombieBehavior, resolveNpcBehaviorProfile, resolveNpcFaction } from '../systems/npcBehaviorTreeSystem';

export type MysteryRoomFacing = 'up' | 'down' | 'left' | 'right';
export type MysteryRoomMovementMode = 'idle' | 'manual' | 'auto';
export type MysteryRoomAnimationState = 'idle' | 'walk';
export type MysteryRoomDoorState = 'closed' | 'opening' | 'open' | 'closing' | 'locked';
export type MysteryRoomHeroAlertState = 'clear' | 'suspicious' | 'spotted';
export type MysteryRoomGameStatus = 'running' | 'respawning' | 'game_over';
export type { MysteryRoomGameMode } from '../world/mapTypes';
export type MysteryRoomBotDifficulty = 'low' | 'mid' | 'high';
export type MysteryRoomGlobalMissionKey = 'evac' | 'last_voice' | 'cleanup';
export type MysteryRoomNpcAwarenessState = 'patrol' | 'suspicious' | 'alert' | 'search';
export type MysteryRoomIncidentKind =
  | 'fire'
  | 'door_failure'
  | 'reactor_fault'
  | 'server_fault'
  | 'injured_person'
  | 'alarm_call'
  | 'alarm';
export type MysteryRoomIncidentStatus = 'active' | 'assigned' | 'resolving' | 'resolved';
export type MysteryRoomIncidentSourceType = 'system' | 'player';
export type MysteryRoomNpcRoleAction =
  | 'repair_door'
  | 'repair_server'
  | 'repair_reactor'
  | 'treat_injury'
  | 'trigger_alarm'
  | 'investigate_intruder'
  | 'stop_experiment'
  | 'evacuate_lab'
  | 'evacuate_if_clear';
export type MysteryRoomNpcTaskKind =
  | 'patrol'
  | 'work'
  | 'rest'
  | 'toilet'
  | 'wander'
  | 'respond_fire'
  | 'repair_door'
  | 'repair_server'
  | 'repair_reactor'
  | 'treat_injury'
  | 'trigger_alarm'
  | 'evacuate'
  | 'investigate'
  | 'neutralize_intruder';

export type MysteryRoomInventoryItemKind = MysteryRoomPickupItemKind;

export type MysteryRoomInventoryItemState = {
  id: MysteryRoomInventoryItemId;
  label: string;
  kind: MysteryRoomInventoryItemKind;
  isWeapon: boolean;
  acquiredAtMs: number;
  quantity: number;
  slotIndex: number;
  icon: string;
  iconAssetId?: string;
  iconAssetUrl?: string;
  iconAssetName?: string;
  iconAssetMimeType?: string;
  description?: string;
  sourceObjectId?: string;
};

export type MysteryRoomIncidentState = {
  id: string;
  kind: MysteryRoomIncidentKind;
  zoneId: string;
  position: MysteryRoomVec2;
  sourceType: MysteryRoomIncidentSourceType;
  sourceActorId: 'system' | 'hero' | string;
  severity: number;
  status: MysteryRoomIncidentStatus;
  assignedNpcId: string | null;
  leaseUntilMs: number;
  createdAtMs: number;
  resolvedAtMs: number | null;
  resolutionAction: MysteryRoomNpcRoleAction | null;
  resolutionRemainingMs: number;
};

export type MysteryRoomWorldAlarmState = {
  state: 'clear' | 'active';
  sourceIncidentId: string | null;
  activeUntilMs: number;
};

export type MysteryRoomTrespassWarningState = {
  zoneId: string;
  zoneTitle: string;
  requiredAccess: MysteryRoomAccessLevel;
  detectedByNpcId: string;
  warningStartedAtMs: number;
  warningUntilMs: number;
  escalatedAtMs: number | null;
};

export type MysteryRoomBodyReportState = {
  npcId: string;
  discoveredByNpcId: string;
  discoveredAtMs: number;
  guardCalled: boolean;
  medicCalled: boolean;
};

export type MysteryRoomSecurityState = {
  trespass: MysteryRoomTrespassWarningState | null;
  bodyReports: MysteryRoomBodyReportState[];
};

export type MysteryRoomNpcTaskState = {
  kind: MysteryRoomNpcTaskKind;
  score: number;
  enterThreshold: number;
  exitThreshold: number;
  takeoverMargin: number;
  minHoldMs: number;
  assignedIncidentId: string | null;
  targetNodeId: string | null;
  targetPoint?: MysteryRoomVec2 | null;
  targetZoneId?: string | null;
  anchorId?: string | null;
  anchorPoint?: MysteryRoomVec2 | null;
  taskAreaShape?: 'circle' | 'square';
  taskAreaRadius?: number;
};

export type MysteryRoomNpcTaskQueueEntry = {
  id: string;
  npcId: string;
  npcName: string;
  role: MysteryRoomNpcRole;
  kind: MysteryRoomNpcTaskKind | 'duty_wait';
  state: 'active' | 'waiting' | 'idle';
  label: string;
  anchorId: string | null;
  targetZoneId: string | null;
  activation: NonNullable<MysteryRoomNpcState['dutyActivation']>;
};

export type MysteryRoomInputState = {
  move: MysteryRoomVec2;
  pointerTarget: MysteryRoomVec2 | null;
};

export type MysteryRoomHeroState = {
  position: MysteryRoomVec2;
  velocity: MysteryRoomVec2;
  facing: MysteryRoomFacing;
  zoneId: string;
  hidden: boolean;
  hideZoneId: string | null;
  movementMode: MysteryRoomMovementMode;
  accessLevels: MysteryRoomAccessLevel[];
  animationState: MysteryRoomAnimationState;
  alertState: MysteryRoomHeroAlertState;
  spottedByNpcIds: string[];
  speed: number;
  radius: number;
  health: number;
  maxHealth: number;
  alive: boolean;
  level: number;
  money: number;
  strengthKg: number;
  intelligence: number;
  carriedObjectId: string | null;
  carriedObjectWeightKg: number;
};

export type MysteryRoomRespawnState = {
  startedAtMs: number;
  readyAtMs: number;
  checkpoint: MysteryRoomVec2;
  zoneId: string;
};

export type MysteryRoomInteractableRuntimeState = {
  id: string;
  kind: MysteryRoomInteractableKind;
  zoneId: string;
  label: string;
  worldLabelVisibility?: MysteryRoomWorldLabelVisibility;
  state: 'idle' | 'used';
  layer?: number;
  grantsAccess?: MysteryRoomAccessLevel;
  requiredAccess?: MysteryRoomAccessLevel;
  itemId?: MysteryRoomInventoryItemId;
  pickupKind?: MysteryRoomInventoryItemKind;
  resourceKind?: MysteryRoomResourceKind;
  resourceAmount?: number;
  resourceSourceKind?: MysteryRoomResourceSourceKind;
  inventoryIcon?: string;
  inventoryIconAssetId?: string;
  inventoryIconAssetUrl?: string;
  inventoryIconAssetName?: string;
  inventoryIconAssetMimeType?: string;
  linkedLightIds?: string[];
  lightReaction?: 'toggle' | 'on' | 'off';
  defaultEnabled?: boolean;
  source?: 'interactable' | 'custom_object';
  customObjectId?: string;
  onActivateActions?: MysteryRoomTriggerAction[];
  onPickupActions?: MysteryRoomTriggerAction[];
  anchor: MysteryRoomVec2;
  interactionRadius: number;
};

export type MysteryRoomLightRuntimeState = MysteryRoomLightDefinition & {
  enabled: boolean;
  eventFlickerUntilMs?: number;
};

export type MysteryRoomHazardRuntimeState = MysteryRoomHazardDefinition;

export type MysteryRoomTriggerRuntimeState = MysteryRoomMapDefinition['triggers'][number] & {
  lastActivatedAtMs: number | null;
};

export type MysteryRoomSoundSourceRuntimeState = MysteryRoomMapDefinition['soundSources'][number] & {
  active: boolean;
  lastPlayedAtMs: number | null;
};

export type MysteryRoomDoorRuntimeState = {
  id: string;
  label: string;
  requiredAccess: MysteryRoomAccessLevel;
  accessMode: MysteryRoomDoorAccessMode;
  accessCardObjectIds: string[];
  fromZoneId: string;
  toZoneId: string;
  state: MysteryRoomDoorState;
  openProgress: number;
  panel: {
    x: number;
    y: number;
    width: number;
    height: number;
  };
  trigger: {
    x: number;
    y: number;
    width: number;
    height: number;
  };
  orientation: 'horizontal' | 'vertical';
  holdOpenMs: number;
};

export type MysteryRoomNpcState = {
  id: string;
  name: string;
  role: MysteryRoomNpcRole;
  actorKind: 'bot' | 'boss';
  actorPreset: string;
  faction: MysteryRoomNpcFaction;
  behaviorProfile: MysteryRoomNpcBehaviorProfile;
  position: MysteryRoomVec2;
  velocity: MysteryRoomVec2;
  facing: MysteryRoomFacing;
  zoneId: string;
  routeId: string | null;
  routeNodeIndex: number;
  pauseRemainingMs: number;
  animationState: MysteryRoomAnimationState;
  awarenessState: MysteryRoomNpcAwarenessState;
  seesHero: boolean;
  suspicionMs: number;
  suspiciousScanElapsedMs: number;
  investigateTarget: MysteryRoomVec2 | null;
  searchRemainingMs: number;
  accessLevels: MysteryRoomAccessLevel[];
  visionRange: number;
  visionHalfAngleDeg: number;
  speed: number;
  radius: number;
  color: number;
  health: number;
  maxHealth: number;
  scale: number;
  invisible: boolean;
  alive: boolean;
  killedAtMs: number | null;
  homeZoneId: string;
  workAnchorIds: string[];
  evacuationNodeId: string;
  dutyActivation: 'auto' | 'manual' | 'trigger' | 'door_open' | 'world_alarm';
  dutyTriggerIds: string[];
  dutyDoorIds: string[];
  currentTask: MysteryRoomNpcTaskState | null;
  taskLockedUntilMs: number;
  taskCooldowns: Partial<Record<MysteryRoomNpcTaskKind, number>>;
  lastTaskRecalcAtMs: number;
  navPath: string[];
  navIndex: number;
  currentTargetNodeId: string | null;
  currentRoleAction: MysteryRoomNpcRoleAction | null;
  roleActionRemainingMs: number;
  stuckMs: number;
  lastWaypointDistance: number | null;
};

export type MysteryRoomProjectileState = {
  id: string;
  kind: 'bullet';
  from: MysteryRoomVec2;
  to: MysteryRoomVec2;
  createdAtMs: number;
  ttlMs: number;
  hitKind: 'none' | 'wall' | 'door' | 'object' | 'npc' | 'player' | 'bot';
  hitId?: string;
};

export type MysteryRoomImpactEffectState = {
  id: string;
  kind: 'spark' | 'blood_splash' | 'blood_pool';
  position: MysteryRoomVec2;
  radius: number;
  createdAtMs: number;
  ttlMs: number;
  sourceId?: string;
};

export type MysteryRoomVisibilityState = {
  origin: MysteryRoomVec2;
  polygon: MysteryRoomVec2[];
  blockingSegments: MysteryRoomSegment[];
  radius?: number;
};

export type MysteryRoomLocalBotState = {
  id: string;
  name: string;
  position: MysteryRoomVec2;
  velocity: MysteryRoomVec2;
  facing: MysteryRoomFacing;
  zoneId: string;
  target: MysteryRoomVec2;
  speed: number;
  radius: number;
  scale: number;
  color: number;
  animationState: MysteryRoomAnimationState;
  decisionAtMs: number;
  lastAttackAtMs: number;
  patrolArea?: MysteryRoomRect | null;
  aggression: number;
  /** P2P: позиции приходят с хоста / от игрока — без локального ИИ-блуждания */
  remotePeer?: boolean;
  /** Кэш маршрута при преследовании героя (обход дверей и стен). */
  chaseSteerTarget?: MysteryRoomVec2;
  chasePathHeroPos?: MysteryRoomVec2;
  chasePathRecalcAtMs?: number;
};

export type MysteryRoomLocalRuntime = {
  mode: 'local';
  gameplay: {
    gameMode: MysteryRoomGameMode;
    botDifficulty: MysteryRoomBotDifficulty;
  };
  zombieMode: {
    enabled: boolean;
    totalWaves: number;
    waveSize: number;
    spawnIntervalMs: number;
    waveBreakMs: number;
    warmupMs: number;
    currentWave: number;
    remainingToSpawn: number;
    nextSpawnAtMs: number;
    nextWaveAtMs: number | null;
    completed: boolean;
  };
  globalMission: {
    active: boolean;
    key: MysteryRoomGlobalMissionKey;
    title: string;
    description: string;
    target: number;
    current: number;
    completed: boolean;
    rewardMoney: number;
  };
  gameStatus: MysteryRoomGameStatus;
  elapsedMs: number;
  respawn: MysteryRoomRespawnState | null;
  map: MysteryRoomMapDefinition;
  hero: MysteryRoomHeroState;
  input: MysteryRoomInputState;
  doors: MysteryRoomDoorRuntimeState[];
  interactables: MysteryRoomInteractableRuntimeState[];
  inventory: MysteryRoomInventoryItemState[];
  activeInventoryItemId: MysteryRoomInventoryItemId | null;
  flashlightEnabled: boolean;
  lastShotAtMs: number;
  projectiles: MysteryRoomProjectileState[];
  impactEffects: MysteryRoomImpactEffectState[];
  lights: MysteryRoomLightRuntimeState[];
  hazards: MysteryRoomHazardRuntimeState[];
  triggers: MysteryRoomTriggerRuntimeState[];
  soundSources: MysteryRoomSoundSourceRuntimeState[];
  npcs: MysteryRoomNpcState[];
  npcTaskQueue: MysteryRoomNpcTaskQueueEntry[];
  incidents: MysteryRoomIncidentState[];
  worldAlarm: MysteryRoomWorldAlarmState;
  security: MysteryRoomSecurityState;
  visibility: MysteryRoomVisibilityState | null;
  events: string[];
  localBots: MysteryRoomLocalBotState[];
  debug: {
    showGeometry: boolean;
  };
};

export type MysteryRoomLocalRuntimeOptions = {
  labScale?: number;
  mapDefinition?: MysteryRoomMapDefinition;
  botCount?: number;
  botsOnly?: boolean;
  gameMode?: MysteryRoomGameMode;
  botDifficulty?: MysteryRoomBotDifficulty;
  globalMission?: MysteryRoomGlobalMissionKey;
  zombieWaveCount?: number;
  zombieWaveSize?: number;
};

function isRuntimeLightLinkedToSwitch(light: MysteryRoomLightRuntimeState, switchInteractable: MysteryRoomInteractableRuntimeState) {
  const linkedLightIds = new Set(switchInteractable.linkedLightIds ?? []);
  if (linkedLightIds.has(light.id)) {
    return true;
  }
  if (light.switchId === switchInteractable.id || light.switchIds?.includes(switchInteractable.id)) {
    return true;
  }
  const hasExplicitSwitch = Boolean(light.switchId || light.switchIds?.length);
  return !hasExplicitSwitch && light.zoneId === switchInteractable.zoneId;
}

function applyInitialLightSwitchDefaults(
  lights: MysteryRoomLightRuntimeState[],
  interactables: MysteryRoomInteractableRuntimeState[],
): MysteryRoomLightRuntimeState[] {
  const switchDefaults = interactables.filter(
    (entry) => entry.kind === 'light_switch' && typeof entry.defaultEnabled === 'boolean',
  );
  if (switchDefaults.length === 0) {
    return lights;
  }

  return lights.map((light) => {
    const controllingSwitch = switchDefaults.find((entry) => isRuntimeLightLinkedToSwitch(light, entry));
    return controllingSwitch ? { ...light, enabled: controllingSwitch.defaultEnabled ?? light.enabled } : light;
  });
}

function createCustomObjectRuntimeInteractable(
  object: MysteryRoomCustomObjectDefinition,
  usedIds: Set<string>,
): MysteryRoomInteractableRuntimeState {
  const id = usedIds.has(object.id) ? `object:${object.id}` : object.id;
  usedIds.add(id);
  const anchor = rectCenter(object.rect);
  const pickupKind = object.pickupKind ?? (
    object.resourceKind
      ? 'resource'
      : object.kind === 'weapon'
        ? 'weapon'
        : object.kind === 'badge'
          ? 'access_card'
          : undefined
  );
  const isPickup = Boolean(pickupKind || object.itemId || object.resourceKind);
  return {
    id,
    kind: isPickup ? 'pickup' : 'inspect',
    zoneId: object.zoneId,
    label: object.label || object.id,
    worldLabelVisibility: object.worldLabelVisibility,
    state: 'idle',
    layer: object.layer,
    source: 'custom_object',
    customObjectId: object.id,
    grantsAccess: object.grantsAccess,
    itemId: isPickup ? object.itemId ?? (object.resourceKind ? `resource_${object.resourceKind}` as MysteryRoomInventoryItemId : object.id) : undefined,
    pickupKind,
    resourceKind: object.resourceKind,
    resourceAmount: normalizeRuntimeResourceAmount(object.resourceAmount),
    resourceSourceKind: object.resourceSourceKind,
    inventoryIcon: object.inventoryIcon,
    inventoryIconAssetId: object.inventoryIconAssetId,
    inventoryIconAssetUrl: object.inventoryIconAssetUrl,
    inventoryIconAssetName: object.inventoryIconAssetName,
    inventoryIconAssetMimeType: object.inventoryIconAssetMimeType,
    linkedLightIds: object.linkedLightIds ? [...object.linkedLightIds] : undefined,
    onActivateActions: object.onActivateActions ? [...object.onActivateActions] : undefined,
    onPickupActions: object.onPickupActions ? [...object.onPickupActions] : undefined,
    anchor,
    interactionRadius: Math.max(72, Math.min(220, Math.max(object.rect.width, object.rect.height) * 0.65)),
  };
}

export function createMysteryRoomLocalRuntime(
  mapId = 'lisad_9_night_shift',
  options: MysteryRoomLocalRuntimeOptions = {},
): MysteryRoomLocalRuntime {
  const gameMode = resolveGameModeOption(options.gameMode);
  const botDifficulty = resolveBotDifficultyOption(options.botDifficulty);
  const globalMission = resolveGlobalMissionOption(options.globalMission);
  const missionState = buildInitialGlobalMissionState(globalMission);
  const missionActive = typeof options.globalMission !== 'undefined';
  const zombieMode = buildInitialZombieModeState(gameMode, options);
  const baseMap = normalizeMysteryRoomMapDefinition(options.mapDefinition ?? getMysteryRoomMapDefinition(mapId));
  const shouldApplyGameplayVariant =
    baseMap.id === 'lisad_9_night_shift' &&
    (typeof options.gameMode !== 'undefined' || typeof options.globalMission !== 'undefined');
  const variantBaseMap = shouldApplyGameplayVariant ? applyGameplayVariantToMap(baseMap, gameMode, globalMission) : baseMap;
  const map = options.labScale && options.labScale !== 1 ? scaleMysteryRoomMap(variantBaseMap, options.labScale) : variantBaseMap;
  const heroSpawnSlot = pickHeroSpawnSlot(map);
  const baseInteractables: MysteryRoomInteractableRuntimeState[] = map.interactables.map((entry) => ({
    id: entry.id,
    kind: entry.kind,
    zoneId: entry.zoneId,
    label: entry.label,
    worldLabelVisibility: entry.worldLabelVisibility,
    state: 'idle',
    layer: entry.layer,
    grantsAccess: entry.grantsAccess,
    requiredAccess: entry.requiredAccess,
    itemId: entry.itemId,
    resourceKind: entry.resourceKind,
    resourceAmount: normalizeRuntimeResourceAmount(entry.resourceAmount),
    resourceSourceKind: entry.resourceSourceKind,
    inventoryIcon: entry.inventoryIcon,
    inventoryIconAssetId: entry.inventoryIconAssetId,
    inventoryIconAssetUrl: entry.inventoryIconAssetUrl,
    inventoryIconAssetName: entry.inventoryIconAssetName,
    inventoryIconAssetMimeType: entry.inventoryIconAssetMimeType,
    linkedLightIds: entry.linkedLightIds ? [...entry.linkedLightIds] : undefined,
    lightReaction: entry.lightReaction,
    defaultEnabled: entry.defaultEnabled,
    source: 'interactable',
    onActivateActions: entry.onActivateActions ? [...entry.onActivateActions] : undefined,
    onPickupActions: entry.onPickupActions ? [...entry.onPickupActions] : undefined,
    anchor: { ...entry.anchor },
    interactionRadius: entry.interactionRadius,
  }));
  const usedInteractableIds = new Set(baseInteractables.map((entry) => entry.id));
  const customObjectInteractables = map.customObjects
    .filter((object) => object.interactive)
    .map((object) => createCustomObjectRuntimeInteractable(object, usedInteractableIds));
  const interactables: MysteryRoomInteractableRuntimeState[] = [
    ...baseInteractables,
    ...customObjectInteractables,
  ];
  const lights = applyInitialLightSwitchDefaults(
    map.lights.map((light) => ({
      ...light,
      position: { ...light.position },
      enabled: light.enabled ?? true,
      eventFlickerUntilMs: 0,
    })),
    interactables,
  );

  return {
    mode: 'local',
    gameplay: {
      gameMode,
      botDifficulty,
    },
    zombieMode,
    globalMission: { ...missionState, active: missionActive },
    gameStatus: 'running',
    elapsedMs: 0,
    respawn: null,
    map,
    hero: {
      velocity: { x: 0, y: 0 },
      facing: 'down',
      position: { x: heroSpawnSlot.x, y: heroSpawnSlot.y },
      zoneId: heroSpawnSlot.room ?? heroSpawnSlot.zoneId ?? map.spawnZoneId,
      hidden: false,
      hideZoneId: null,
      movementMode: 'idle',
      accessLevels: ['public', 'lab'],
      animationState: 'idle',
      alertState: 'clear',
      spottedByNpcIds: [],
      speed: 148,
      radius: 18,
      health: 100,
      maxHealth: 100,
      alive: true,
      level: 1,
      money: 0,
      strengthKg: 50,
      intelligence: 50,
      carriedObjectId: null,
      carriedObjectWeightKg: 0,
    },
    input: {
      move: { x: 0, y: 0 },
      pointerTarget: null,
    },
    doors: map.doors.map((door) => ({
      id: door.id,
      label: door.label ?? door.id,
      requiredAccess: door.requiredAccess,
      accessMode: door.accessMode ?? (door.requiredAccess === 'public' ? 'public' : 'card_required'),
      accessCardObjectIds: [...(door.accessCardObjectIds ?? [])],
      fromZoneId: door.fromZoneId,
      toZoneId: door.toZoneId,
      state: 'closed',
      openProgress: 0,
      panel: { ...door.panel },
      trigger: { ...door.trigger },
      orientation: door.orientation,
      holdOpenMs: 0,
    })),
    interactables,
    inventory: [],
    activeInventoryItemId: null,
    flashlightEnabled: false,
    lastShotAtMs: Number.NEGATIVE_INFINITY,
    projectiles: [],
    impactEffects: [],
    lights,
    hazards: map.hazards.map((hazard) => ({
      ...hazard,
      position: { ...hazard.position },
    })),
    triggers: map.triggers.map((trigger) => ({
      ...trigger,
      rect: { ...trigger.rect },
      actions: trigger.actions.map((action) => ({ ...action })),
      lastActivatedAtMs: null,
    })),
    soundSources: map.soundSources.map((source) => ({
      ...source,
      position: { ...source.position },
      active: !!source.loop,
      lastPlayedAtMs: source.loop ? 0 : null,
    })),
    npcs: map.npcs.map((npc) => {
      const actorKind = npc.actorKind ?? 'bot';
      const actorPreset = npc.actorPreset ?? 'npc';
      const behaviorSource = { ...npc, actorKind, actorPreset };
      const behaviorProfile = resolveNpcBehaviorProfile(behaviorSource);
      const faction = resolveNpcFaction(behaviorSource);
      const difficultyMultiplier = botDifficultyMultiplier(botDifficulty);
      const modeMultiplier = gameModeNpcMultiplier(gameMode, behaviorProfile);
      const zombieVisionTight = npcUsesZombieBehavior({ ...behaviorSource, behaviorProfile }) ? 0.62 : 1;
      const visionRange =
        positiveRuntimeNumber(npc.visionRange, actorKind === 'boss' ? 720 : 560) *
        difficultyMultiplier *
        modeMultiplier.vision *
        zombieVisionTight;
      const speedBase = positiveRuntimeNumber(npc.speed, actorKind === 'boss' ? 88 : 118);
      const speed =
        (npcUsesZombieBehavior({ ...behaviorSource, behaviorProfile }) ? 0.5 : 1) *
        speedBase *
        difficultyMultiplier *
        modeMultiplier.speed;
      const baseHealth = positiveRuntimeNumber(npc.health, actorKind === 'boss' ? 500 : 100);
      return {
        id: npc.id,
        name: npc.name,
        role: npc.role,
        actorKind,
        actorPreset,
        faction,
        behaviorProfile,
        position: { ...npc.spawn },
        velocity: { x: 0, y: 0 },
        facing: 'down',
        zoneId: resolveInitialZoneId(map, npc.spawn),
        routeId: npc.routeId ?? null,
        routeNodeIndex: 0,
        pauseRemainingMs: 0,
        animationState: 'idle',
        awarenessState: 'patrol',
        seesHero: false,
        suspicionMs: 0,
        suspiciousScanElapsedMs: 0,
        investigateTarget: null,
        searchRemainingMs: 0,
        accessLevels: [...npc.accessLevels],
        visionRange,
        visionHalfAngleDeg: positiveRuntimeNumber(npc.visionHalfAngleDeg, actorKind === 'boss' ? 62 : 42),
        speed,
        radius: positiveRuntimeNumber(npc.radius, actorKind === 'boss' ? 34 : 16),
        color: npc.color,
        health: Math.round(baseHealth * difficultyMultiplier * modeMultiplier.health),
        maxHealth: Math.round(
          positiveRuntimeNumber(npc.maxHealth ?? npc.health, actorKind === 'boss' ? 500 : 100) *
            difficultyMultiplier *
            modeMultiplier.health,
        ),
        scale: positiveRuntimeNumber(npc.scale, actorKind === 'boss' ? 1.9 : 1),
        invisible: npc.invisible ?? false,
        alive: true,
        killedAtMs: null,
        homeZoneId: npc.homeZoneId,
        workAnchorIds: [...npc.workAnchorIds],
        evacuationNodeId: npc.evacuationNodeId,
        dutyActivation: npc.dutyActivation ?? 'auto',
        dutyTriggerIds: [...(npc.dutyTriggerIds ?? [])],
        dutyDoorIds: [...(npc.dutyDoorIds ?? [])],
        currentTask: null,
        taskLockedUntilMs: 0,
        taskCooldowns: {},
        lastTaskRecalcAtMs: 0,
        navPath: [],
        navIndex: 0,
        currentTargetNodeId: null,
        currentRoleAction: null,
        roleActionRemainingMs: 0,
        stuckMs: 0,
        lastWaypointDistance: null,
      };
    }),
    npcTaskQueue: [],
    incidents: [],
    worldAlarm: {
      state: 'clear',
      sourceIncidentId: null,
      activeUntilMs: 0,
    },
    security: {
      trespass: null,
      bodyReports: [],
    },
    visibility: null,
    events: [],
    localBots: createLocalBots(map, {
      ...options,
      gameMode,
      botDifficulty,
    }),
    debug: {
      showGeometry: true,
    },
  };
}

function resolveInitialZoneId(map: MysteryRoomMapDefinition, point: MysteryRoomVec2) {
  const room = map.rooms.find((candidate) => candidate.floor.some((floor) => rectContainsPoint(floor, point)));

  if (!room) {
    throw new Error(
      `Mystery Room NPC spawn point is outside all room floors for map "${map.id}": (${point.x}, ${point.y})`,
    );
  }

  return room.id;
}

function positiveRuntimeNumber(value: number | null | undefined, fallback: number): number {
  if (typeof value !== 'number' || !Number.isFinite(value) || value <= 0) {
    return fallback;
  }
  return Math.round(value * 10) / 10;
}

function normalizeRuntimeResourceAmount(value: number | null | undefined): number | undefined {
  if (typeof value !== 'number' || !Number.isFinite(value) || value <= 0) {
    return undefined;
  }
  return Math.max(1, Math.floor(value));
}

const LOCAL_BOT_COLORS = [0x60a5fa, 0xf472b6, 0xfbbf24, 0x34d399, 0xa78bfa, 0xf87171, 0x22d3ee] as const;

function clampBotCount(value: number | undefined, max: number): number {
  if (typeof value !== 'number' || !Number.isFinite(value) || value <= 0) {
    return 0;
  }
  return Math.min(Math.max(0, Math.floor(value)), max);
}

// Зачем: спавним wandering-ботов в spawn-комнате карты, чтобы лобби с ботами было играбельно без сетевого Colyseus.
function createLocalBots(
  map: MysteryRoomMapDefinition,
  options: MysteryRoomLocalRuntimeOptions,
): MysteryRoomLocalBotState[] {
  const mode = resolveGameModeOption(options.gameMode);
  if (mode === 'infection' && (options.zombieWaveCount ?? 0) > 0) {
    return [];
  }
  const difficulty = resolveBotDifficultyOption(options.botDifficulty);
  const modeBonus = mode === 'survival' ? 2 : mode === 'infection' ? 3 : mode === 'stealth' ? -1 : 0;
  const desired = clampBotCount((options.botCount ?? 0) + modeBonus, 7);
  if (desired === 0) {
    return [];
  }
  const patrolAreas = resolveBotPatrolAreas(map, mode);
  if (patrolAreas.length === 0) {
    return [];
  }
  const speedMultiplier = botDifficultyMultiplier(difficulty) * gameModeBotSpeedMultiplier(mode);
  const aggression = gameModeBotAggression(mode, difficulty);
  const botSpawnSlots = map.spawnSlots.filter((slot) => slot.actor === 'bot');
  const bots: MysteryRoomLocalBotState[] = [];
  for (let index = 0; index < desired; index += 1) {
    const patrolArea = patrolAreas[index % patrolAreas.length];
    const spawnSlot = botSpawnSlots[index % Math.max(1, botSpawnSlots.length)];
    const point = spawnSlot
      ? {
          x: Math.round(spawnSlot.x + (Math.random() - 0.5) * 48),
          y: Math.round(spawnSlot.y + (Math.random() - 0.5) * 48),
        }
      : pickRandomPointInsideRect(patrolArea, 36 + (index % 4) * 8);
    bots.push({
      id: `local_bot_${index + 1}`,
      name: `Бот ${index + 1}`,
      position: { ...point },
      velocity: { x: 0, y: 0 },
      facing: 'down',
      zoneId: resolveInitialZoneId(map, point),
      target: { ...point },
      speed: Math.round((110 + Math.floor(Math.random() * 30)) * speedMultiplier),
      radius: 16,
      scale: 1,
      color: LOCAL_BOT_COLORS[index % LOCAL_BOT_COLORS.length],
      animationState: 'idle',
      decisionAtMs: 0,
      lastAttackAtMs: 0,
      patrolArea,
      aggression,
    });
  }
  return bots;
}

function pickHeroSpawnSlot(map: MysteryRoomMapDefinition): MysteryRoomMapDefinition['spawnSlots'][number] {
  const slots = map.spawnSlots.length > 0
    ? map.spawnSlots
    : [{ x: map.spawn.x, y: map.spawn.y, room: map.spawnZoneId, zoneId: map.spawnZoneId, actor: 'player' as const, side: 'A' as const }];
  const playerASlot = slots.find((slot) => (slot.actor ?? 'player') === 'player' && (slot.side ?? 'A') === 'A');
  const anyPlayerSlot = slots.find((slot) => (slot.actor ?? 'player') === 'player');
  return playerASlot ?? anyPlayerSlot ?? slots[0]!;
}

function resolveGameModeOption(value: MysteryRoomGameMode | undefined): MysteryRoomGameMode {
  return value === 'survival' || value === 'stealth' || value === 'infection' ? value : 'coop';
}

function resolveBotDifficultyOption(value: MysteryRoomBotDifficulty | undefined): MysteryRoomBotDifficulty {
  return value === 'low' || value === 'high' ? value : 'mid';
}

function resolveGlobalMissionOption(value: MysteryRoomGlobalMissionKey | undefined): MysteryRoomGlobalMissionKey {
  return value === 'last_voice' || value === 'cleanup' ? value : 'evac';
}

function buildInitialZombieModeState(
  gameMode: MysteryRoomGameMode,
  options: MysteryRoomLocalRuntimeOptions,
): MysteryRoomLocalRuntime['zombieMode'] {
  const configuredWaveCount = typeof options.zombieWaveCount === 'number' && Number.isFinite(options.zombieWaveCount)
    ? Math.max(0, Math.floor(options.zombieWaveCount))
    : 0;
  const enabled = gameMode === 'infection' && configuredWaveCount > 0;
  const totalWaves = enabled ? Math.min(60, Math.max(1, configuredWaveCount)) : 0;
  const configuredWaveSize = typeof options.zombieWaveSize === 'number' && Number.isFinite(options.zombieWaveSize)
    ? Math.max(1, Math.floor(options.zombieWaveSize))
    : Math.max(4, Math.min(20, (options.botCount ?? 6) + 2));
  return {
    enabled,
    totalWaves,
    waveSize: Math.min(30, Math.max(1, configuredWaveSize)),
    spawnIntervalMs: 1_250,
    waveBreakMs: 5_000,
    warmupMs: 2_000,
    currentWave: 0,
    remainingToSpawn: 0,
    nextSpawnAtMs: enabled ? 2_000 : Number.POSITIVE_INFINITY,
    nextWaveAtMs: null,
    completed: !enabled,
  };
}

function buildInitialGlobalMissionState(key: MysteryRoomGlobalMissionKey): MysteryRoomLocalRuntime['globalMission'] {
  if (key === 'last_voice') {
    return {
      active: true,
      key,
      title: 'Последний голос',
      description: 'Найдите источник сигнала: серверный аудит и реакторную консоль.',
      target: 2,
      current: 0,
      completed: false,
      rewardMoney: 1800,
    };
  }
  if (key === 'cleanup') {
    return {
      active: true,
      key,
      title: 'Зачистка сектора',
      description: 'Устраните все активные угрозы на объекте.',
      target: 1,
      current: 0,
      completed: false,
      rewardMoney: 2200,
    };
  }
  return {
    active: true,
    key,
    title: 'Эвакуация образца',
    description: 'Заберите образец и вернитесь в шлюз для эвакуации.',
    target: 2,
    current: 0,
    completed: false,
    rewardMoney: 1600,
  };
}

function applyGameplayVariantToMap(
  map: MysteryRoomMapDefinition,
  gameMode: MysteryRoomGameMode,
  globalMission: MysteryRoomGlobalMissionKey,
): MysteryRoomMapDefinition {
  const missionSpawn = resolveMissionSpawn(map, globalMission);
  const visibilityScale = gameMode === 'stealth' ? 0.78 : gameMode === 'infection' ? 1.16 : gameMode === 'survival' ? 0.92 : 1;
  let next: MysteryRoomMapDefinition = {
    ...map,
    spawn: missionSpawn?.position ? { ...missionSpawn.position } : { ...map.spawn },
    spawnZoneId: missionSpawn?.zoneId ?? map.spawnZoneId,
    visibilityRadius: Math.max(220, Math.round(map.visibilityRadius * visibilityScale)),
    interactables: map.interactables.map((entry) => ({ ...entry, anchor: { ...entry.anchor } })),
    hazards: map.hazards.map((entry) => ({ ...entry, position: { ...entry.position } })),
    lights: map.lights.map((entry) => ({ ...entry, position: { ...entry.position } })),
    soundSources: map.soundSources.map((entry) => ({ ...entry, position: { ...entry.position } })),
  };

  if (gameMode === 'stealth') {
    next.lights = next.lights.map((light) =>
      light.id === 'cross_light_core' || light.id === 'cross_light_south' ? { ...light, enabled: false } : light,
    );
  }
  if (gameMode === 'survival') {
    next.hazards = appendUniqueById(next.hazards, {
      id: 'survival_choke_smoke',
      kind: 'smoke',
      zoneId: 'central_cross',
      position: { x: 2048, y: 1350 },
      radius: 170,
      intensity: 0.9,
    });
  }
  if (gameMode === 'infection') {
    next.hazards = appendUniqueById(next.hazards, {
      id: 'infection_bloom',
      kind: 'smoke',
      zoneId: 'central_cross',
      position: { x: 2048, y: 1120 },
      radius: 220,
      intensity: 1,
    });
  }

  if (globalMission === 'last_voice') {
    const signalSoundId = resolveSignalSoundId(next);
    if (signalSoundId) {
      next.soundSources = appendUniqueById(next.soundSources, {
        id: 'mission_last_voice_signal',
        label: 'Аварийный сигнал',
        zoneId: 'reactor_room',
        position: { x: 2500, y: 1940 },
        soundId: signalSoundId,
        radius: 620,
        volume: 0.92,
        loop: true,
      });
    }
  }

  next.interactables = appendMissionInteractables(next.interactables, globalMission);
  next.customObjects = appendMissionCustomObjects(next.customObjects, gameMode, globalMission);
  return next;
}

function resolveMissionSpawn(
  map: MysteryRoomMapDefinition,
  mission: MysteryRoomGlobalMissionKey,
): { zoneId: string; position: MysteryRoomVec2 } | null {
  if (mission === 'last_voice') {
    return { zoneId: 'server_room', position: { x: 1360, y: 1710 } };
  }
  if (mission === 'cleanup') {
    return { zoneId: 'central_cross', position: { x: 2048, y: 1120 } };
  }
  return { zoneId: 'airlock_entry', position: { x: 560, y: 1160 } };
}

function appendMissionInteractables(
  interactables: MysteryRoomMapDefinition['interactables'],
  mission: MysteryRoomGlobalMissionKey,
): MysteryRoomMapDefinition['interactables'] {
  const next = [...interactables];
  const add = (entry: MysteryRoomMapDefinition['interactables'][number]) => {
    if (!next.some((current) => current.id === entry.id)) {
      next.push(entry);
    }
  };
  if (mission === 'evac') {
    add({
      id: 'mission_evac_beacon',
      kind: 'inspect',
      zoneId: 'airlock_entry',
      anchor: { x: 500, y: 1180 },
      interactionRadius: 92,
      label: 'Маяк эвакуации',
      worldLabelVisibility: 'near_interactive',
    });
  } else if (mission === 'last_voice') {
    add({
      id: 'mission_signal_locator',
      kind: 'inspect',
      zoneId: 'reactor_room',
      anchor: { x: 2580, y: 1910 },
      interactionRadius: 92,
      label: 'Локатор сигнала',
      worldLabelVisibility: 'near_interactive',
    });
  } else {
    add({
      id: 'mission_cleanup_board',
      kind: 'inspect',
      zoneId: 'central_cross',
      anchor: { x: 2100, y: 1200 },
      interactionRadius: 88,
      label: 'Панель зачистки',
      worldLabelVisibility: 'near_interactive',
    });
  }
  return next;
}

function appendMissionCustomObjects(
  source: MysteryRoomMapDefinition['customObjects'],
  gameMode: MysteryRoomGameMode,
  mission: MysteryRoomGlobalMissionKey,
): MysteryRoomMapDefinition['customObjects'] {
  const next = [...source];
  const add = (entry: MysteryRoomMapDefinition['customObjects'][number]) => {
    if (!next.some((current) => current.id === entry.id)) {
      next.push(entry);
    }
  };

  if (gameMode === 'stealth') {
    add(createVariantCustomObject('variant_stealth_cover', 'central_cross', 1940, 1200, 180, 52, 'Укрытие тени', 0x1f2937));
  }
  if (gameMode === 'survival') {
    add(createVariantCustomObject('variant_survival_barricade', 'central_cross', 1980, 1320, 220, 60, 'Баррикада', 0x7c2d12));
  }
  if (gameMode === 'infection') {
    add(createVariantCustomObject('variant_infection_nest', 'central_cross', 2100, 1100, 160, 120, 'Заражённый узел', 0x7f1d1d));
  }

  if (mission === 'evac') {
    add(createVariantCustomObject('mission_evac_capsule', 'airlock_entry', 430, 1120, 120, 120, 'Эвак-капсула', 0x0f766e));
  } else if (mission === 'last_voice') {
    add(createVariantCustomObject('mission_voice_relay', 'reactor_room', 2520, 1880, 84, 84, 'Ретранслятор сигнала', 0x1d4ed8));
  } else {
    add(createVariantCustomObject('mission_cleanup_cache', 'central_cross', 2160, 1180, 96, 72, 'Пульт зачистки', 0x92400e));
  }

  return next;
}

function createVariantCustomObject(
  id: string,
  zoneId: string,
  x: number,
  y: number,
  width: number,
  height: number,
  label: string,
  color: number,
): MysteryRoomMapDefinition['customObjects'][number] {
  return {
    id,
    kind: 'equipment',
    zoneId,
    rect: { x, y, width, height },
    rotation: 0,
    color,
    shape: { kind: 'rect' },
    label,
    collision: { kind: 'none' },
    collisionEnabled: false,
    physicsEnabled: false,
    blocksSight: false,
    interactive: false,
    tags: ['variant'],
  };
}

function appendUniqueById<T extends { id: string }>(source: T[], entry: T): T[] {
  if (source.some((current) => current.id === entry.id)) {
    return source;
  }
  return [...source, entry];
}

function resolveSignalSoundId(map: MysteryRoomMapDefinition): string | null {
  const alarm = map.soundDefinitions.find((entry) => entry.kind === 'alarm') ?? map.soundDefinitions[0];
  return alarm?.id ?? null;
}

function botDifficultyMultiplier(difficulty: MysteryRoomBotDifficulty): number {
  if (difficulty === 'low') return 0.85;
  if (difficulty === 'high') return 1.2;
  return 1;
}

function gameModeNpcMultiplier(mode: MysteryRoomGameMode, behaviorProfile: string): { speed: number; vision: number; health: number } {
  if (mode === 'stealth') {
    return { speed: 0.88, vision: 0.8, health: 0.92 };
  }
  if (mode === 'survival') {
    return { speed: 1.16, vision: 1.14, health: 1.12 };
  }
  if (mode === 'infection') {
    const zombieLike = behaviorProfile === 'zombie' || behaviorProfile === 'hostile';
    // Зомби: медленнее и короче видят; не «супер-чувство» через всю карту.
    return zombieLike ? { speed: 1, vision: 0.58, health: 1.22 } : { speed: 0.94, vision: 0.96, health: 0.95 };
  }
  return { speed: 1, vision: 1, health: 1 };
}

function gameModeBotSpeedMultiplier(mode: MysteryRoomGameMode): number {
  if (mode === 'stealth') return 0.88;
  if (mode === 'survival') return 1.18;
  if (mode === 'infection') return 0.92;
  return 1;
}

function gameModeBotAggression(mode: MysteryRoomGameMode, difficulty: MysteryRoomBotDifficulty): number {
  const base = mode === 'stealth' ? 0.45 : mode === 'survival' ? 1.05 : mode === 'infection' ? 1.35 : 0.75;
  const diff = difficulty === 'low' ? 0.7 : difficulty === 'high' ? 1.2 : 1;
  return Math.max(0.2, Math.min(2.5, base * diff));
}

function resolveBotPatrolAreas(map: MysteryRoomMapDefinition, mode: MysteryRoomGameMode): MysteryRoomRect[] {
  const roomById = new Map(map.rooms.map((room) => [room.id, room]));
  const spawnRoom = roomById.get(map.spawnZoneId) ?? map.rooms[0] ?? null;
  const roomFloors = (id: string) => roomById.get(id)?.floor ?? [];

  if (mode === 'infection') {
    return map.rooms.flatMap((room) => room.floor);
  }
  if (mode === 'survival') {
    const cross = roomFloors('central_cross');
    const server = roomFloors('server_room');
    const reactor = roomFloors('reactor_room');
    const fallback = spawnRoom?.floor ?? [];
    return [...cross, ...server, ...reactor, ...fallback];
  }
  if (mode === 'stealth') {
    const research = roomFloors('research_lab');
    const med = roomFloors('med_bay');
    const cross = roomFloors('central_cross');
    const fallback = spawnRoom?.floor ?? [];
    return [...research, ...med, ...cross, ...fallback];
  }
  return [...(spawnRoom?.floor ?? []), ...roomFloors('central_cross')];
}

function pickRandomPointInsideRect(rect: { x: number; y: number; width: number; height: number }, padding: number): MysteryRoomVec2 {
  const safePadding = Math.min(padding, Math.max(0, rect.width / 2 - 8), Math.max(0, rect.height / 2 - 8));
  const x = rect.x + safePadding + Math.random() * Math.max(0, rect.width - safePadding * 2);
  const y = rect.y + safePadding + Math.random() * Math.max(0, rect.height - safePadding * 2);
  return { x, y };
}

export function setMysteryRoomLocalMoveInput(
  runtime: MysteryRoomLocalRuntime,
  nextMove: MysteryRoomVec2,
): MysteryRoomLocalRuntime {
  return {
    ...runtime,
    input: {
      ...runtime.input,
      move: nextMove,
    },
  };
}

export function setMysteryRoomLocalPointerTarget(
  runtime: MysteryRoomLocalRuntime,
  pointerTarget: MysteryRoomVec2 | null,
): MysteryRoomLocalRuntime {
  return {
    ...runtime,
    input: {
      ...runtime.input,
      pointerTarget,
    },
  };
}

export function moveMysteryRoomInventoryItem(
  runtime: MysteryRoomLocalRuntime,
  itemId: MysteryRoomInventoryItemId,
  beforeItemId: MysteryRoomInventoryItemId | null,
): MysteryRoomLocalRuntime {
  if (itemId === beforeItemId) {
    return runtime;
  }

  const movingItem = runtime.inventory.find((item) => item.id === itemId);
  if (!movingItem) {
    return runtime;
  }

  const remainingItems = runtime.inventory.filter((item) => item.id !== itemId);
  const rawInsertIndex = beforeItemId ? remainingItems.findIndex((item) => item.id === beforeItemId) : remainingItems.length;
  const insertIndex = rawInsertIndex >= 0 ? rawInsertIndex : remainingItems.length;
  const nextInventory = [...remainingItems];
  nextInventory.splice(insertIndex, 0, movingItem);

  return {
    ...runtime,
    inventory: nextInventory.map((item, slotIndex) => ({ ...item, slotIndex })),
  };
}
